import moneyTypeApi from 'api/moneyTypeApi'
import { Checkbox, Space, Typography } from 'antd'
import { selectFormatNumber } from 'features/System/screens/SystemConfiguration/SystemConfigurationSlice'
import CustomAntButton from 'general/components/Button/CustomAntButton'
import CustomAntSelect from 'general/components/CustomAntSelect'
import CustomLabel from 'general/components/CustomLabel'
import InputNumber from 'general/components/Forms/InputNumber'
import ToastHelper from 'general/helpers/ToastHelper'
import Utils from 'general/utils/Utils'
import { useAppSelector } from 'hooks/useRedux'
import useRouter from 'hooks/useRouter'
import { useMemo, useRef, useState } from 'react'
import KTTooltip from 'general/components/eInvoiceComponents/KTTooltip'

function InvoiceTableGeneralInfo(props) {
  const {
    formik,
    currencies,
    discountTypeArray,
    handleSelectDiscountType,
    checkedHHDV,
    onChangeCheckHHDV,
    checkedPayment,
    onChangePayment,
    onToggleMulTaxRate,
    mulTaxRate,
    autoCalculate,
    currency,
    invoiceTemplateIdNewYear,
    setInvoiceTemplateIdNewYear,
  } = props

  const formatNumber = useAppSelector(selectFormatNumber)
  const [exchangeRateSource, setExchangeRateSource] = useState('catalog')
  const [isExchangeRateSourceOpen, setIsExchangeRateSourceOpen] =
    useState(false)
  const exchangeRateRequestId = useRef(0)

  const { pathname } = useRouter()
  const isDeliveryBill = useMemo(
    () => pathname.includes('phieu-xuat-kho'),
    [pathname],
  )

  const fetchExchangeRate = async (curr) => {
    if (curr?.toUpperCase() === 'VND') {
      exchangeRateRequestId.current += 1
      return
    }

    const requestId = ++exchangeRateRequestId.current

    try {
      const response = await moneyTypeApi.latestExchangeRate(curr)
      const numericRate = Number(response?.moneyType?.exchangeRate)

      if (!Number.isFinite(numericRate) || numericRate <= 0) {
        throw new Error('Dữ liệu tỷ giá Vietcombank không hợp lệ')
      }

      if (requestId === exchangeRateRequestId.current) {
        formik.setFieldValue(
          'exchangeRate',
          Utils.roundNumber(numericRate, formatNumber?.exchangeRate),
        )
      }
    } catch (error) {
      if (requestId === exchangeRateRequestId.current) {
        ToastHelper.showError(
          error?.response?.data?.message ||
            error?.message ||
            'Không thể lấy tỷ giá từ Vietcombank',
        )
      }
    }
  }

  const setCatalogExchangeRate = (curr) => {
    const currentCurrency = currencies.find((option) => option.value === curr)
    formik.setFieldValue(
      'exchangeRate',
      Utils.roundNumber(
        currentCurrency?.exchangeRate ?? 1,
        formatNumber?.exchangeRate,
      ),
    )
  }

  const handleSelectCurrency = (curr) => {
    formik.setFieldValue('currency', curr)

    if (curr?.toUpperCase() === 'VND') {
      exchangeRateRequestId.current += 1
      setCatalogExchangeRate(curr)
      return
    }

    if (exchangeRateSource === 'vietcombank') {
      fetchExchangeRate(curr)
      return
    }

    setCatalogExchangeRate(curr)
  }

  const handleSelectExchangeRateSource = (source) => {
    setExchangeRateSource(source)

    if (source === 'catalog') {
      exchangeRateRequestId.current += 1
      setCatalogExchangeRate(formik.values.currency)
      return
    }

    if (formik.values.currency?.toUpperCase() === 'VND') {
      exchangeRateRequestId.current += 1
      setCatalogExchangeRate(formik.values.currency)
      return
    }

    fetchExchangeRate(formik.values.currency)
  }

  const exchangeRateSourceOptions = [
    {
      value: 'catalog',
      label: 'Lấy tỷ giá trên danh mục',
    },
    {
      value: 'vietcombank',
      label: 'Lấy tỷ giá từ Vietcombank',
    },
  ]

  return (
    <div className="my-5">
      <Typography.Title className="w-100" style={{ fontSize: 18 }}>
        Hàng hoá/ Dịch vụ
      </Typography.Title>
      {/* Checkbox group */}
      <Space size={'large'} className="w-100" wrap>
        <KTTooltip text="Hiện cột “Tính chất hàng hóa dịch vụ”">
          <span>
            <Checkbox checked={checkedHHDV} onChange={onChangeCheckHHDV}>
              Hiện cột "Tính chất HHDV"
            </Checkbox>
          </span>
        </KTTooltip>
        <Checkbox
          checked={autoCalculate}
          onChange={() => {
            formik.setFieldValue('validateTableData', 1)
            formik.setFieldValue('autoCalculate', !autoCalculate)
          }}
        >
          Tự động tính số liệu điều chỉnh
        </Checkbox>

        {invoiceTemplateIdNewYear?.category?.form == 3 ? (
          <></>
        ) : (
          <>
            <Checkbox checked={checkedPayment} onChange={onChangePayment}>
              Đã thanh toán
            </Checkbox>
            {formik.values.taxType === 3 && (
              <Checkbox checked={mulTaxRate} onChange={onToggleMulTaxRate}>
                Nhiều thuế suất
              </Checkbox>
            )}
            {currency?.transactionForeign && (
              <>
                <div className="d-flex flex-row align-items-center w-100 gap-2">
                  <CustomLabel text="Loại tiền:" width="100%"></CustomLabel>
                  <CustomAntSelect
                    className="w-100"
                    autoMatchWidth={270}
                    showSearch={false}
                    options={currencies
                      .filter((v) => v.label?.toUpperCase() !== 'VNĐ')
                      .map((v) => ({
                        ...v,
                        label: v.label,
                        value: v.value,
                      }))}
                    optionColumns={[
                      { title: 'Mã loại tiền', dataIndex: 'label', width: 100 },
                      {
                        title: 'Tên loại tiền',
                        dataIndex: 'name',
                        render: (name, option) =>
                          name === 'đồng' && option.value === 'VND'
                            ? 'Việt Nam đồng'
                            : name || option.label,
                      },
                    ]}
                    value={formik.values.currency}
                    onChange={(value) => handleSelectCurrency(value)}
                  />
                </div>
                <div className="d-flex flex-row align-items-center w-165px">
                  <CustomLabel text="Tỷ giá:" width={100}></CustomLabel>
                  <InputNumber
                    systemConfigType="exchangeRate"
                    name="exchangeRate"
                    tooltipText={String(formik.values.exchangeRate ?? '')}
                    disabled={formik.values.currency === 'VND'}
                    fieldProps={formik.getFieldProps('exchangeRate')}
                    fieldMeta={formik.getFieldMeta('exchangeRate')}
                    fieldHelper={formik.getFieldHelpers('exchangeRate')}
                    className="w-100"
                    onChangeInput={(event) => {
                      console.log('event', event?.target.values)
                      let exchangeRate = Utils.roundNumber(
                        Number(
                          event?.target.value
                            ?.replaceAll('.', '')
                            ?.replace(',', '.'),
                        ),
                        formatNumber?.exchangeRate,
                      )
                      formik.setFieldValue('exchangeRate', exchangeRate)
                    }}
                  />
                  <div
                    className="ml-2"
                    style={{
                      position: 'relative',
                      flex: '0 0 32px',
                      width: 32,
                    }}
                  >
                    <CustomAntButton
                      iconCn="fa-regular fa-gear fa-lg text-black-50"
                      antProps={{
                        type: 'text',
                        title: 'Thiết lập tỷ giá',
                        className: 'p-0 bg-transparent-i',
                        shape: 'round',
                        onClick: () =>
                          setIsExchangeRateSourceOpen((isOpen) => !isOpen),
                      }}
                      customStyle={{
                        minWidth: 'unset',
                        padding: 4,
                        border: 0,
                        background: 'transparent',
                        boxShadow: 'none',
                        color: 'inherit',
                      }}
                    />
                    {isExchangeRateSourceOpen && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          zIndex: 1050,
                          width: 32,
                          opacity: 0,
                        }}
                      >
                        <CustomAntSelect
                          bordered={false}
                          showArrow={false}
                          showSearch={false}
                          isOpen={isExchangeRateSourceOpen}
                          onOpenChange={setIsExchangeRateSourceOpen}
                          autoMatchWidth={220}
                          placement="bottomRight"
                          options={exchangeRateSourceOptions}
                          value={exchangeRateSource}
                          onChange={(source) => {
                            handleSelectExchangeRateSource(source)
                            setIsExchangeRateSourceOpen(false)
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
            <div
              className={`flex-row align-items-center w-100 gap-2 ${isDeliveryBill ? 'd-none' : 'd-flex'}`}
            >
              <CustomLabel text="Chiết khấu:" width="100%"></CustomLabel>
              <CustomAntSelect
                autoMatchWidth={200}
                value={formik.values.discountType}
                options={discountTypeArray.map((v) => ({
                  label: v.label,
                  value: v.value,
                }))}
                onChange={(value) => handleSelectDiscountType(value)}
                showSearch={false}
              />
            </div>
            {/* <div className="d-flex flex-row align-items-center w-100 gap-2">
          <CustomLabel text="Cố định cột:" width="100%"></CustomLabel>
          <CustomAntSelect
            autoMatchWidth={150}
            value={persistent}
            options={[
              { label: 'Số lượng', value: 'quantityPersistent' },
              { label: 'Đơn giá sau thuế', value: 'priceAfterTaxPersistent' },
            ]}
            onChange={(value) => setPersistent(value)}
            showSearch={false}
          />
        </div> */}
          </>
        )}
      </Space>
    </div>
  )
}

export default InvoiceTableGeneralInfo
